import { useEffect, useState } from 'react';
import { useStore } from '../context/StoreContext';
import { Product } from '../types';
import { ProductCard } from '../components/catalog/ProductCard';
import { Search, SlidersHorizontal, RefreshCw } from 'lucide-react';

interface CatalogPageProps {
  onNavigate: (path: string) => void;
  initialSearch?: string;
  initialCategory?: string;
}

export function CatalogPage({ onNavigate, initialSearch = '', initialCategory = '' }: CatalogPageProps) {
  const { categories } = useStore();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(initialSearch);
  const [selectedCategory, setSelectedCategory] = useState(initialCategory);
  const [selectedType, setSelectedType] = useState<string>('');
  const [sort, setSort] = useState('featured');
  const [total, setTotal] = useState(0);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (selectedCategory) params.set('category', selectedCategory);
    if (selectedType) params.set('type', selectedType);
    if (sort) params.set('sort', sort);

    fetch(`/api/products?${params.toString()}`)
      .then((res) => res.json())
      .then((data: any) => {
        setProducts(data.products || []);
        setTotal(data.pagination?.total || 0);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [search, selectedCategory, selectedType, sort]);

  const handleClearFilters = () => {
    setSearch('');
    setSelectedCategory('');
    setSelectedType('');
    setSort('featured');
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-8">
      {/* Header */}
      <div className="border-b border-border-subtle pb-8">
        <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-primary">
          Acervo Completo
        </span>
        <h1 className="text-3xl sm:text-4xl font-serif font-normal text-ink mt-1">
          Catálogo & Criações
        </h1>
        <p className="text-xs sm:text-sm text-ink-muted mt-2 max-w-2xl leading-relaxed">
          Navegue por nossas cerâmicas utilitárias, têxteis em linho puro, arquivos digitais com licença de uso e consultorias de ambientação autoral.
        </p>
      </div>

      {/* Filter Controls Bar */}
      <div className="bg-surface border border-border-subtle p-4 rounded-xs flex flex-col md:flex-row gap-4 items-stretch md:items-center justify-between">
        {/* Search */}
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 text-ink-muted absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nome, acabamento ou material..."
            className="w-full bg-background border border-border-subtle pl-9 pr-4 py-2 text-xs rounded-xs focus:outline-none focus:border-primary"
          />
        </div>

        {/* Filters Group */}
        <div className="flex flex-wrap items-center gap-3">
          {/* Category Dropdown */}
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="bg-background border border-border-subtle px-3 py-2 text-xs rounded-xs focus:outline-none focus:border-primary text-ink"
          >
            <option value="">Todas as Categorias</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.slug}>
                {cat.name}
              </option>
            ))}
          </select>

          {/* Product Type Dropdown */}
          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            className="bg-background border border-border-subtle px-3 py-2 text-xs rounded-xs focus:outline-none focus:border-primary text-ink"
          >
            <option value="">Todos os Tipos</option>
            <option value="physical">Peças Físicas</option>
            <option value="digital">Downloads Digitais</option>
            <option value="service">Serviços & Consultorias</option>
          </select>

          {/* Sort Dropdown */}
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="bg-background border border-border-subtle px-3 py-2 text-xs rounded-xs focus:outline-none focus:border-primary text-ink"
          >
            <option value="featured">Destaques do Ateliê</option>
            <option value="newest">Mais Recentes</option>
            <option value="price_asc">Menor Preço</option>
            <option value="price_desc">Maior Preço</option>
            <option value="title_asc">Nome (A - Z)</option>
          </select>

          {(search || selectedCategory || selectedType || sort !== 'featured') && (
            <button
              onClick={handleClearFilters}
              className="text-xs text-ink-muted hover:text-ink flex items-center gap-1 px-2 py-1 rounded-xs hover:bg-surface-muted transition-colors"
              title="Limpar todos os filtros"
            >
              <RefreshCw className="w-3 h-3" /> Limpar
            </button>
          )}
        </div>
      </div>

      {/* Result Count and Grid */}
      <div>
        <div className="flex items-center justify-between mb-6 text-xs text-ink-muted">
          <span>
            Mostrando <strong className="text-ink font-medium">{products.length}</strong> de <strong className="text-ink font-medium">{total}</strong> {total === 1 ? 'produto' : 'produtos'}
          </span>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <div key={i} className="animate-pulse space-y-3">
                <div className="aspect-[4/5] bg-surface-muted rounded-xs" />
                <div className="h-4 bg-surface-muted rounded-xs w-2/3" />
                <div className="h-3 bg-surface-muted rounded-xs w-1/3" />
              </div>
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="bg-surface border border-border-subtle rounded-xs p-12 text-center space-y-4 max-w-lg mx-auto">
            <SlidersHorizontal className="w-10 h-10 text-ink-muted mx-auto stroke-1" />
            <h3 className="font-serif text-lg font-medium text-ink">Nenhum produto encontrado</h3>
            <p className="text-xs text-ink-muted leading-relaxed">
              Não encontramos nenhuma peça correspondente aos filtros selecionados. Tente buscar com termos mais amplos.
            </p>
            <button
              onClick={handleClearFilters}
              className="bg-ink text-ink-inverted px-6 py-2.5 rounded-xs text-xs uppercase tracking-wider font-medium hover:bg-primary transition-colors"
            >
              Limpar Filtros
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {products.map((product) => (
              <ProductCard key={product.id} product={product} onNavigate={onNavigate} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
